/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:no-magic-numbers

import { Dictionary, ICONS_URL } from '@gh/common-shared';
import { AutocompleteItem } from '@gh/common-shared-data';
import { DEFAULT_CURRENCY_TEMPLATE } from '@gh/config';
import { coalesce, isNotNil, splitLikeNumber, substitute } from '@gh/core-util';

import { ICellRendererParams, ProcessRowParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { constant, get, isEmpty, isFunction, isNil, isNumber, isString, keys, negate, padStart, pickBy } from 'lodash';

export const HEADER_CELL_TEXT_RIGHT_CLASS = 'header-cell--text-right';
export const CELL_NO_OVERFLOW_CLASS = 'cell--no-overflow';
export const CELL_PRIMARY_CLASS = 'cell--primary';
export const CELL_TEXT_PRIMARY_CLASS = 'cell--text-primary';
export const CELL_FOOTER_TITLE_CLASS = 'cell--footer-title';
export const CELL_TEXT_RIGHT_CLASS = 'cell--text-right';
export const CELL_TEXT_CENTER_CLASS = 'cell--text-center';
export const CELL_TEXT_RIGHT_OVERFLOW_CLASS = 'cell--text-right-overflow';
export const CELL_FIRST_COLUMN_CLASS = 'cell--first-column';
export const CELL_FIRST_DRAGGABLE_COLUMN_CLASS = 'cell--first-draggable-column';

export const ROW_DELETED_CLASS = 'deleted';
export const ROW_NEW_CLASS = 'new-status';
export const ROW_ACTIVE_CLASS = 'active-status';
export const ROW_DEACTIVATED_CLASS = 'deactivated-status';
export const ROW_BLOCKED_CLASS = 'blocked-status';
export const ROW_IN_DELETION_CLASS = 'in-deletion';
export const ROW_UNREAD_CLASS = 'unread';
export const ROW_WARNING_CLASS = 'row--warning';
export const ROW_SELECTED_CLASS = 'row--selected';
export const ROW_DIRTY_CLASS = 'row--dirty';
export const ROW_DIRTY_MARKER_CLASS = 'row--dirty-marker';
export const ROW_GROUP_DIRTY_CLASS = 'row-group--dirty';
export const ROW_DISABLED_CLASS = 'row--disabled';
export const ROW_DIVIDER_CLASS = 'row--divider';
export const ROW_HIDDEN_CLASS = 'row--hidden';
export const GREEN_MARKER_COLOR = '#4caf50';
export const RED_MARKER_COLOR = '#af0000';
export const LIGHTGREEN_MARKER_COLOR = '#74cc77';
export const PINK_MARKER_COLOR = '#F48EB1';
export const ROW_FOOTER_TITLE_CLASS = 'row--footer-title';
export const ROW_FOOTER_SUMMARY_CLASS = 'row--footer-dark';
export const ROW_FOOTER_LIGHT_CLASS = 'row--footer-light';
export const ROW_GROUP_CLASS = 'row--group';
export const ROW_BOLD_CLASS = 'row--bold';
export const ROW_TOP_CLASS = 'row--top';

export const EDITABLE_CELL_GREEN_CLASS = 'editable-cell--green';
export const EDITABLE_CELL_YELLOW_CLASS = 'editable-cell--yellow';
export const EDITABLE_CELL_INVALID_CLASS = 'editable-cell--invalid';

export const rendererIconByName = (icon: string, color: string = 'curentColor'): string =>
  `<div class="grid__icon" style="color: ${color}"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
       <use xlink:href="${ICONS_URL}#${icon}"/>
   </svg></div>`;

/**
 * Renders svg colored with HEX circle.
 * @param hex
 * @returns {string}
 */
export const rendererColorCircle = (hex: string): string =>
  `<div class="grid__icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
       <circle cx="12" cy="12" r="7" fill="${hex}" />
   </svg></div>`;

export const renderColoredArrow = (hex: string, isUp: boolean = true): string => {
  const rotateDegree = isUp ? 90 : -90;
  const xTranslate = isUp ? -157 : 0;
  const yTranslate = isUp ? 0 : 200;
  return `<div class="grid__icon"><svg xmlns="http://www.w3.org/2000/svg">
      <path d="M0 0h24v24H0z" fill="none" transform="translate(${xTranslate}, ${yTranslate}) rotate(${rotateDegree} ${rotateDegree} ${rotateDegree})"/>
      <path
        fill="${hex}"
        d="M21 11H6.83l3.58-3.59L9 6l-6 6 6 6 1.41-1.41L6.83 13H21z" 
        transform="translate(${xTranslate}, ${yTranslate}) rotate(${rotateDegree} ${rotateDegree} ${rotateDegree})"
      />
    </svg></div>`;
};

/**
 * Returns svg crossed circle
 * @returns {string}
 */
export const rendererCrossedCircle = () =>
  `<div class="grid__icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
       <circle cx="12" cy="12" r="7" fill="none" stroke="#777" stroke-width="2"/>
       <line x1="20" y1="4" x2="4" y2="20" stroke-width="2" stroke="#777"/>
   </svg></div>`;

export const conditionalCellRenderer = (condition: (value: any) => boolean, html: string) =>
  (params: any): string => condition(params.value) ? html : '';
export const truthyCellRenderer = (html: string) => conditionalCellRenderer(Boolean, html);
export const falsyCellRenderer = (html: string) => conditionalCellRenderer(negate(Boolean), html);
export const emptyCellRenderer = (html: string) => conditionalCellRenderer(isEmpty, html);
export const notEmptyCellRenderer = (html: string) => conditionalCellRenderer(negate(isEmpty), html);
export const noneCellRenderer = () => '';
export const identityCellRenderer = ({ valueFormatted, value }: ICellRendererParams) => coalesce(valueFormatted, value);
export const outerLabelCellRendererDecorator = (label: string | TransformFn<any, string>,
                                                offset: number = 20) => (cellRenderer: Function) => {
  const labelFn = isString(label) ? constant(label) : label;
  return (params: any) =>
    `<span class="outer-label-cell__label" style="padding-right: ${offset}px">${labelFn(params)}:</span>
     ${cellRenderer(params)}`;
};

// tslint:disable-next-line:max-line-length
export const innerLabelCellRendererDecorator = (label: string | TransformFn<any, string>) => (cellRenderer: Function) => {
  const labelFn = isString(label) ? constant(label) : label;
  return (params: any) =>
    `<span class="inner-label-cell__label">${labelFn(params)}:</span>
     ${cellRenderer(params)}`;
};

export const warnLabelCellTemplate = (label: string) =>
  `<div class="warn-label inline-block">${label}</div>`;

export const defaultCurrencyCellRendererDecorator = (cellRenderer: Function) =>
  (params: any) => {
    const price = cellRenderer(params);
    return isNotNil(price) ? substitute(DEFAULT_CURRENCY_TEMPLATE, splitLikeNumber(price)) : '';
  };

export const falsyAsEmptyCellRenderer = ({ value, valueFormatted }: ICellRendererParams) =>
  !value ? '' : (valueFormatted || coalesce(value, ''));

export const cssRules = (rules: Hash<boolean>) => keys(pickBy(rules)).join(' ');

export const identityValueFormatter = ({ value }: ValueFormatterParams) => value;
export const dictionaryValueFormatter = (dict: Dictionary<any>) =>
  (params: any) => isNil(params.value) ? ' ' : dict.get(params.value);
export const autocompleteValueFormatter = ({ value }: { value?: AutocompleteItem }) => value && value.name || '';
export const codeFormatterFactory = (n: number) => ({ value }: { value: string }) =>
  isNotNil(value) ? padStart(value, n, '0') : '';
export const code3Formatter = codeFormatterFactory(3); // tslint:disable-line:no-magic-numbers
export const suffixFormatterDecorator = (formatter: TransformFn<ValueFormatterParams, string>, suffix: string) =>
  (params: ValueFormatterParams) => `${formatter(params)} ${suffix}`;
export const cellValueFormatter = <T>(formatter: TransformFn<T, string>) =>
  (params: ValueFormatterParams) => formatter(params.value);
export const defaultCurrencyValueFormatterDecorator = (valueFormatter: Function) =>
  (params: any) => {
    const price = valueFormatter(params);
    return isNotNil(price) ? substitute(DEFAULT_CURRENCY_TEMPLATE, splitLikeNumber(price)) : '';
  };
export const editableValueFormatterDecorator = (valueFormatter: Function) =>
  (params: any) => valueFormatter({ ...params, value: params.value.value });

export const cellValueNumberPredicate = (predicate: PredicateFn<number>) =>
  ({ value }: { value: number }) => predicate(value);
export const isCellValueNegative = cellValueNumberPredicate((value) => value < 0);
export const isCellValuePositive = cellValueNumberPredicate((value) => value > 0);

/**
 * Renders sanitized HTML
 */
export const htmlCellRenderer = ({ value }: ICellRendererParams) =>
  (value || '').toString().replace(/<\/?script>/g, '');

/**
 * Renders only text of HTML content (cleans cell value from all HTML tags)
 */
export const htmlTextCellRenderer = ({ value }: ICellRendererParams) =>
  (value || '').toString().replace(/<[^>]*>/g, '');

/**
 * Renderer for cell that have look and feel of non-editable cell.
 * It allows to style the same area as editable cell has.
 * For example, this renderer can be used to modify background of cell.
 */
export const likeEditableCellRenderer = ({ value, valueFormatted }: ICellRendererParams) =>
  `<div class="like-editable-cell">${coalesce(valueFormatted, coalesce(value, ''))}</div>`;

export const linkCellRendererFactory = (target: string = '_self') => ({ value }: ICellRendererParams) =>
  `<a href="${value}" target="${target}">${value}</a>`;

export const textOverlayRowPostCreate = (text: string) => (params: ProcessRowParams) => {
  if (!params.eRow) {
    return;
  }

  const overlayEl = document.createElement('div');
  overlayEl.classList.add('grid-row-text-overlay');
  overlayEl.appendChild(document.createTextNode(text));
  params.eRow.appendChild(overlayEl);
};

export const conditionalRowPostCreateDecorator = (predicate: PredicateFn<ProcessRowParams>,
                                                  rowPostProcessor: ProcedureFn<ProcessRowParams>) =>
  (params: ProcessRowParams) => {
    if (predicate(params)) {
      rowPostProcessor(params);
    }
  };

export const pinnedRowPostCreateDecorator = (rowPostProcessor: ProcedureFn<ProcessRowParams>) =>
  conditionalRowPostCreateDecorator(({ node }) => node.isRowPinned(), rowPostProcessor);

export const gridPropetyValueGetter = (propName: string | TransformFn<any, any>) =>
  isFunction(propName)
    ? ({ data }: ValueGetterParams) => propName(data)
    : ({ data }: ValueGetterParams) => get(data, propName);

export const gridPositiveValueGetter = ({ data, colDef: { field } }: ValueGetterParams) => {
  // data can be null when data are loaded lazily
  const value = data && field ? data[field] : void 0;
  return isNumber(value) ? Math.abs(value) : value;
};
